/**
 * Avatar Component
 * User avatar display
 */

interface AvatarUser {
  id: string;
  display_name: string;
  email?: string;
  avatar_url: string | null;
}

interface AvatarProps {
  user: AvatarUser;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

const Avatar = ({ user, size = 'md', className = '' }: AvatarProps) => {
  const sizeClasses = {
    xs: 'w-6 h-6 text-[10px]',
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-24 h-24 text-2xl',
  };

  // Get first letter of display name or email
  const initial = (user.display_name || user.email || '?')
    .charAt(0)
    .toUpperCase();

  // Generate consistent color based on user ID
  const getBackgroundColor = () => {
    const colors = [
      'bg-gradient-to-br from-blue-500 to-blue-700',
      'bg-gradient-to-br from-green-500 to-emerald-700',
      'bg-gradient-to-br from-purple-500 to-violet-700',
      'bg-gradient-to-br from-pink-500 to-rose-700',
      'bg-gradient-to-br from-amber-400 to-orange-600',
      'bg-gradient-to-br from-indigo-500 to-blue-700',
      'bg-gradient-to-br from-red-500 to-rose-700',
      'bg-gradient-to-br from-teal-400 to-cyan-600',
    ];
    const hash = user.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  if (user.avatar_url) {
    return (
      <img
        src={user.avatar_url}
        alt={user.display_name}
        className={`${sizeClasses[size]} rounded-full object-cover ring-2 ring-white dark:ring-dark-bg-secondary shadow-sm ${className}`}
        onError={(e) => {
          // Fallback to initial if image fails to load
          console.error('Failed to load avatar:', user.avatar_url);
          e.currentTarget.style.display = 'none';
        }}
      />
    );
  }

  return (
    <div
      className={`${sizeClasses[size]} ${getBackgroundColor()} rounded-full flex items-center justify-center font-semibold text-white shadow-sm ${className}`}
    >
      {initial}
    </div>
  );
};

export default Avatar;
